import React, { useEffect, useState, useCallback } from 'react';
import { env } from 'constants/env';
import { useAdyenScript } from 'V2/Shared/hooks/useAdyenScript';
import {
  getAdyenConfig,
  AdyenCheckout,
} from 'V2/Steps/PaymentDetails/getAdyenConfig';
import {
  AdyenCreditCardResponse,
  AdyenACHResponse,
} from 'V2/Steps/PaymentDetails';

declare global {
  interface Window {
    AdyenCheckout: any;
  }
}

type BillingAddress = {
  numberOrName?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode: string;
  countryCode?: string;
};

export type CheckoutEncryptedCard = {
  type: 'ENCRYPTED_CREDIT_CARD';
  cardHolder: string;
  number: string;
  cvc: string;
  expirationMonth: string;
  expirationYear: string;
  billingAddress?: BillingAddress;
};

export type AdyenDropinHandle = {
  submit: () => void;
};

const isACH = (
  e: AdyenCreditCardResponse | AdyenACHResponse
): e is AdyenACHResponse => e.data.paymentMethod.type === 'ach';

function buildCardDetails(e: AdyenCreditCardResponse): CheckoutEncryptedCard {
  const card: CheckoutEncryptedCard = {
    type: 'ENCRYPTED_CREDIT_CARD',
    cardHolder: e.data.paymentMethod.holderName,
    number: e.data.paymentMethod.encryptedCardNumber,
    cvc: e.data.paymentMethod.encryptedSecurityCode,
    expirationMonth: e.data.paymentMethod.encryptedExpiryMonth,
    expirationYear: e.data.paymentMethod.encryptedExpiryYear,
  };

  if (e.data.billingAddress?.postalCode) {
    card.billingAddress = {
      postalCode: e.data.billingAddress.postalCode,
      numberOrName: e.data.billingAddress.houseNumberOrName,
      street: e.data.billingAddress.street,
      city: e.data.billingAddress.city,
      state: e.data.billingAddress.stateOrProvince,
      countryCode: e.data.billingAddress.country,
    };
  }

  return card;
}

interface CheckoutAdyenProps {
  holderName: string;
  onReady: (dropin: AdyenDropinHandle) => void;
  onFormValidChange: (isValid: boolean) => void;
  onSubmit: (paymentToken: string) => void;
  onError?: () => void;
}

export const CheckoutAdyen: React.FC<CheckoutAdyenProps> = ({
  holderName,
  onReady,
  onFormValidChange,
  onSubmit,
  onError,
}) => {
  const { scriptLoaded } = useAdyenScript();
  const [checkout, setCheckout] = useState<AdyenCheckout>();
  const [formLoaded, setFormLoaded] = useState(false);

  const handleAdyenSubmit = useCallback(
    (e: AdyenCreditCardResponse | AdyenACHResponse) => {
      if (isACH(e)) {
        onSubmit(
          JSON.stringify({
            type: 'ENCRYPTED_ACH',
            accountNumber: e.data.paymentMethod.encryptedBankAccountNumber,
            routingNumber: e.data.paymentMethod.encryptedBankLocationId,
            accountOwner: e.data.paymentMethod.ownerName,
          })
        );
      } else {
        onSubmit(JSON.stringify(buildCardDetails(e)));
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    if (!scriptLoaded || formLoaded) return;

    const config = getAdyenConfig({
      paymentMethod: 'credit_card',
      environment: env.adyenEnvironment,
      formData: { holderName },
    });

    async function init() {
      const adyenConfiguration = {
        ...config,
        onChange: (state: { isValid: boolean }) => {
          onFormValidChange(state.isValid);
        },
        onSubmit: handleAdyenSubmit,
        onError: () => {
          onError?.();
        },
      };
      setCheckout(await window.AdyenCheckout(adyenConfiguration));
    }

    init();
  }, [
    scriptLoaded,
    formLoaded,
    holderName,
    handleAdyenSubmit,
    onFormValidChange,
    onError,
  ]);

  useEffect(() => {
    if (!checkout) return;

    const dropin = checkout
      .create('dropin', {
        onReady: () => setFormLoaded(true),
      })
      .mount('#checkout-adyen-dropin');

    onReady({ submit: () => dropin.submit() });

    return () => {
      dropin.unmount();
    };
  }, [checkout, onReady]);

  return <div id="checkout-adyen-dropin" style={{ maxWidth: '100%' }} />;
};
